<h1 mat-dialog-title>{{ isEditing ? 'Edit' : 'Create' }} Scheduled Event</h1>

<div mat-dialog-content class="dialog-content">
  <form [formGroup]="form" class="event-form">
    <!-- Basic Information -->
    <div class="form-section">
      <h3>Basic Information</h3>

      <mat-form-field appearance="outline" class="full-width">
        <mat-label>Event Name</mat-label>
        <input matInput formControlName="name" placeholder="Enter event name">
        <mat-error *ngIf="form.get('name')?.hasError('required')">
          Event name is required
        </mat-error>
        <mat-error *ngIf="form.get('name')?.hasError('maxlength')">
          Event name cannot exceed 100 characters
        </mat-error>
      </mat-form-field>

      <mat-form-field appearance="outline" class="full-width">
        <mat-label>Description</mat-label>
        <textarea matInput formControlName="description" rows="3"
                  placeholder="Enter event description (optional)"></textarea>
        <mat-error *ngIf="form.get('description')?.hasError('maxlength')">
          Description cannot exceed 500 characters
        </mat-error>
      </mat-form-field>

      <mat-form-field appearance="outline" class="full-width">
        <mat-label>Event Type</mat-label>
        <mat-select formControlName="eventType" (selectionChange)="onEventTypeChange()">
          <mat-option *ngFor="let type of eventTypes" [value]="type.value">
            {{ type.label }}
          </mat-option>
        </mat-select>
        <mat-hint *ngIf="form.get('eventType')?.value">
          {{ getEventTypeDescription(form.get('eventType')?.value) }}
        </mat-hint>
        <mat-error *ngIf="form.get('eventType')?.hasError('required')">
          Event type is required
        </mat-error>
      </mat-form-field>

      <mat-slide-toggle formControlName="isActive" class="active-toggle">
        Event is active
      </mat-slide-toggle>
    </div>

    <!-- Schedule Configuration -->
    <div class="form-section">
      <h3>Schedule Configuration</h3>

      <div class="cron-section">
        <mat-form-field appearance="outline" class="cron-input">
          <mat-label>Cron Expression</mat-label>
          <input matInput formControlName="cronExpression"
                 placeholder="* * * * * (min hour day month dayOfWeek)">
          <mat-error *ngIf="form.get('cronExpression')?.hasError('required')">
            Cron expression is required
          </mat-error>
          <mat-error *ngIf="form.get('cronExpression')?.hasError('invalidCron')">
            Invalid cron expression format
          </mat-error>
        </mat-form-field>

        <button mat-stroked-button type="button" (click)="testCronExpression()"
                [disabled]="form.get('cronExpression')?.invalid">
          <mat-icon>schedule</mat-icon>
          Test
        </button>
      </div>

      <div class="cron-presets">
        <h4>Common Schedules:</h4>
        <div class="preset-buttons">
          <button mat-button type="button"
                  *ngFor="let preset of cronPresets"
                  (click)="setCronPreset(preset.value)"
                  class="preset-button">
            {{ preset.label }}
          </button>
        </div>
      </div>

      <mat-form-field appearance="outline" class="full-width">
        <mat-label>Timezone</mat-label>
        <mat-select formControlName="timezone">
          <mat-option *ngFor="let tz of timezones" [value]="tz">
            {{ tz }}
          </mat-option>
        </mat-select>
        <mat-error *ngIf="form.get('timezone')?.hasError('required')">
          Timezone is required
        </mat-error>
      </mat-form-field>
    </div>

    <!-- Event-Specific Configuration -->
    <div class="form-section" *ngIf="form.get('eventType')?.value" formGroupName="configuration">
      <h3>Event Configuration</h3>

      <!-- Backup Configuration -->
      <div *ngIf="form.get('eventType')?.value === 'backup'">
        <mat-checkbox formControlName="includeUploads">
          Include uploaded files in backup
        </mat-checkbox>
        <mat-form-field appearance="outline" class="full-width">
          <mat-label>Compression Level (1-9)</mat-label>
          <input matInput type="number" formControlName="compressionLevel" min="1" max="9">
          <mat-hint>Higher values = better compression, slower speed</mat-hint>
        </mat-form-field>
      </div>

      <!-- Export Configuration -->
      <div *ngIf="form.get('eventType')?.value === 'export'">
        <mat-form-field appearance="outline" class="full-width">
          <mat-label>Export Format</mat-label>
          <mat-select formControlName="format">
            <mat-option value="xlsx">Excel (.xlsx)</mat-option>
            <mat-option value="csv">CSV (.csv)</mat-option>
            <mat-option value="json">JSON (.json)</mat-option>
          </mat-select>
        </mat-form-field>
        <mat-checkbox formControlName="includeImages">
          Include product images
        </mat-checkbox>
      </div>

      <!-- AI Processing Configuration -->
      <div *ngIf="form.get('eventType')?.value === 'ai_processing'">
        <mat-form-field appearance="outline" class="full-width">
          <mat-label>Batch Size</mat-label>
          <input matInput type="number" formControlName="batchSize" min="1" max="1000">
          <mat-hint>Number of items to process in each batch</mat-hint>
        </mat-form-field>
        <mat-form-field appearance="outline" class="full-width">
          <mat-label>AI Model</mat-label>
          <mat-select formControlName="model">
            <mat-option value="gpt-3.5-turbo">GPT-3.5 Turbo</mat-option>
            <mat-option value="gpt-4">GPT-4</mat-option>
            <mat-option value="gpt-4-turbo">GPT-4 Turbo</mat-option>
          </mat-select>
        </mat-form-field>
      </div>

      <!-- Data Cleanup Configuration -->
      <div *ngIf="form.get('eventType')?.value === 'data_cleanup'" class="data-cleanup-config">
        <div class="cleanup-row">
          <mat-form-field appearance="outline" class="half-width">
            <mat-label>Delete files older than (days)</mat-label>
            <input matInput type="number" formControlName="daysOld" min="1" max="365">
            <mat-hint>Files older than this will be deleted</mat-hint>
          </mat-form-field>

          <mat-form-field appearance="outline" class="half-width">
            <mat-label>Cleanup Type</mat-label>
            <mat-select formControlName="cleanupType">
              <mat-option value="export_files">Export Files</mat-option>
              <mat-option value="log_files">Log Files</mat-option>
              <mat-option value="temp_files">Temporary Files</mat-option>
              <mat-option value="backup_files">Backup Files</mat-option>
            </mat-select>
          </mat-form-field>
        </div>

        <mat-form-field appearance="outline" class="full-width">
          <mat-label>Directory Path</mat-label>
          <input matInput formControlName="uploadsDirectory" placeholder="./uploads">
          <mat-hint>Path to the directory to clean up</mat-hint>
        </mat-form-field>

        <div class="file-patterns-section">
          <h4>File Patterns to Include</h4>
          <div class="pattern-chips">
            <mat-chip-list>
              <mat-chip *ngFor="let pattern of filePatterns; let i = index"
                       [removable]="true" (removed)="removeFilePattern(i)">
                {{ pattern }}
                <mat-icon matChipRemove>cancel</mat-icon>
              </mat-chip>
            </mat-chip-list>
          </div>

          <div class="add-pattern">
            <mat-form-field appearance="outline" class="pattern-input">
              <mat-label>Add File Pattern</mat-label>
              <input matInput #patternInput placeholder="*.xlsx"
                     (keyup.enter)="addFilePattern(patternInput.value); patternInput.value=''">
              <mat-hint>Use * for wildcards (e.g., *.xlsx, *.csv)</mat-hint>
            </mat-form-field>
            <button mat-icon-button type="button"
                    (click)="addFilePattern(patternInput.value); patternInput.value=''"
                    [disabled]="!patternInput.value">
              <mat-icon>add</mat-icon>
            </button>
          </div>

          <div class="preset-patterns">
            <span>Quick patterns: </span>
            <button mat-button type="button" (click)="addFilePattern('*.xlsx')" class="pattern-preset">
              Excel Files
            </button>
            <button mat-button type="button" (click)="addFilePattern('*.csv')" class="pattern-preset">
              CSV Files
            </button>
            <button mat-button type="button" (click)="addFilePattern('*.pdf')" class="pattern-preset">
              PDF Files
            </button>
            <button mat-button type="button" (click)="addFilePattern('*.log')" class="pattern-preset">
              Log Files
            </button>
          </div>
        </div>

        <div class="cleanup-options">
          <mat-slide-toggle formControlName="preserveRecent">
            <span class="toggle-label">Preserve recent files (safer option)</span>
            <div class="toggle-hint">When enabled, uses filename date instead of file modification time</div>
          </mat-slide-toggle>

          <mat-slide-toggle formControlName="dryRun">
            <span class="toggle-label">Dry Run Mode</span>
            <div class="toggle-hint">Test mode - shows what would be deleted without actually deleting</div>
          </mat-slide-toggle>
        </div>

        <div class="cleanup-preview" *ngIf="form.get('configuration.dryRun')?.value">
          <mat-icon>info</mat-icon>
          <span>Running in test mode - no files will actually be deleted</span>
        </div>
      </div>

      <!-- Report Generation Configuration -->
      <div *ngIf="form.get('eventType')?.value === 'report_generation'">
        <mat-form-field appearance="outline" class="full-width">
          <mat-label>Report Type</mat-label>
          <mat-select formControlName="reportType">
            <mat-option value="summary">Summary Report</mat-option>
            <mat-option value="detailed">Detailed Report</mat-option>
            <mat-option value="analytics">Analytics Report</mat-option>
          </mat-select>
        </mat-form-field>
        <mat-checkbox formControlName="includeCharts">
          Include charts and graphs
        </mat-checkbox>
      </div>
    </div>
  </form>
</div>

<div mat-dialog-actions class="dialog-actions">
  <button mat-button (click)="cancel()" [disabled]="saving">
    Cancel
  </button>
  <button mat-raised-button color="primary" (click)="save()"
          [disabled]="form.invalid || saving">
    <mat-spinner diameter="20" *ngIf="saving"></mat-spinner>
    <span *ngIf="!saving">{{ isEditing ? 'Update' : 'Create' }} Event</span>
  </button>
</div>
